import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Pagination,
  Grid,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Place } from '../Place';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import axios from 'axios';
import { useDebounce } from '../Map';


const PlaceListWithSearch: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [filteredPlaces, setFilteredPlaces] = useState<Place[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [places, setPlaces] = useState<Place[]>([]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, places]);
  const userInfo: any = JSON.parse(localStorage.getItem("usuario") || "{}");

  const userGoogleId = userInfo?.id;
  const debouncedSearchText = useDebounce(searchText, 500); // 500ms de retraso

  useEffect(() => {
    fetchPlaces();
  }, [debouncedSearchText]); // Agregamos showFavorites a las dependencias

  const fetchPlaces = async () => {
    try {
        console.log("BUSCANDO",searchText)
      let url = `${config.backendUrl}/api/Places`;
      const params = new URLSearchParams();
      const cacheKey = `placesCache_${userGoogleId || 'guest'}`;
      params.append("texto", searchText);
      const fullUrl = `${url}?conImagen=true&&${params.toString()}`;
      
      // Solo usamos caché cuando no hay filtros activos
      const shouldUseCache = !searchText ;
  
      try {
        // Intentamos primero obtener datos del backend
        const response = await axios.get<Place[]>(fullUrl);
        const data = response.data;
        console.log("response",response)

        // Actualizamos el estado y la caché
        setPlaces(data);
        setFilteredPlaces(data);
        
        // Guardamos en caché solo si no hay filtros
        if (shouldUseCache) {
          localStorage.setItem(cacheKey, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching places from backend:', error);
        
        // Solo intentamos usar caché si no hay filtros
        if (shouldUseCache) {
          const cachedData = localStorage.getItem(cacheKey);
          
          if (cachedData) {
            const { data, timestamp } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            const maxCacheAge = 24 * 60 * 60 * 1000; // 24 horas
            
            if (cacheAge < maxCacheAge) {
              console.log('Using cached places data');
              setPlaces(data);
              setFilteredPlaces(data);
              return;
            } else {
              console.log('Cache expired, removing old data');
              localStorage.removeItem(cacheKey);
            }
          }
        }
        
        // Si no hay caché disponible o está expirado, mantener estado actual
        console.log('No cached data available or filters active');
      }
    } catch (error) {
      console.error('Unexpected error in fetchPlaces:', error);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlaces = filteredPlaces.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlaces.length / itemsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handlePlaceClick = (place: Place) => {
    //onPlaceClick(place);
    navigate(`/place/${place.id}`);
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Buscar lugares"
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ backgroundColor: 'white' }}
        />
      </Box>

      {filteredPlaces.length === 0 ? (
        <Typography variant="h6" sx={{ textAlign: 'center', mt: 4 }}>
          No se encontraron lugares
        </Typography>
      ) : (
        <>
<Grid container spacing={3}>
  {currentPlaces.map((place) => (
    <Grid 
      key={place.id}
    >
      <Box sx={{ display: 'flex', height: '100%' }}>
        <Card sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.3s',
          '&:hover': {
            transform: 'scale(1.03)',
            boxShadow: 3
          }
        }}>
                  {place.fotos && place.fotos.length > 0 && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={place.fotos[0]}
                      alt={place.name}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="div">
                      {place.name}
                    </Typography>
                    <Chip 
                      label={place.placeType} 
                      size="small" 
                      sx={{ mb: 1 }} 
                      color="primary"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: 2
                    }}>
                      {place.description}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      onClick={() => handlePlaceClick(place)}
                      fullWidth
                      variant="outlined"
                    >
                      Ver detalles
                    </Button>
                    </Box>

                </Card>
                </Box>

              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PlaceListWithSearch;